/**
 * Very small Markdown reader — enough for our intent synthesis output
 * which is just headings, bold, bullets. No HTML, no escaping
 * headaches. Splits a Markdown string into a tagged-block list that a
 * renderer can map to its DOM of choice.
 *
 * Pure / stringy / tested independently.
 */

export type MdBlock =
  | { type: 'heading'; text: MdInline[] }
  | { type: 'paragraph'; text: MdInline[] }
  | { type: 'list'; items: MdInline[][] };

export type MdInline =
  | { type: 'text'; text: string }
  | { type: 'bold'; text: string };

export function parseMarkdownLite(input: string): MdBlock[] {
  const out: MdBlock[] = [];
  const lines = input.split('\n');
  let listItems: MdInline[][] | null = null;
  const flushList = () => {
    if (listItems !== null) {
      out.push({ type: 'list', items: listItems });
      listItems = null;
    }
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) {
      flushList();
      continue;
    }
    const li = /^[-*]\s+(.*)$/.exec(line);
    if (li) {
      if (listItems === null) listItems = [];
      listItems.push(parseInline(li[1] ?? ''));
      continue;
    }
    flushList();
    const h = /^#{1,3}\s+(.*)$/.exec(line);
    if (h) {
      out.push({ type: 'heading', text: parseInline(h[1] ?? '') });
      continue;
    }
    out.push({ type: 'paragraph', text: parseInline(line) });
  }
  flushList();
  return out;
}

export function parseInline(text: string): MdInline[] {
  const out: MdInline[] = [];
  let i = 0;
  while (i < text.length) {
    const open = text.indexOf('**', i);
    if (open === -1) {
      out.push({ type: 'text', text: text.slice(i) });
      break;
    }
    if (open > i) out.push({ type: 'text', text: text.slice(i, open) });
    const close = text.indexOf('**', open + 2);
    if (close === -1) {
      // Unclosed marker — emit as text so the user can see what they typed.
      out.push({ type: 'text', text: text.slice(open) });
      break;
    }
    out.push({ type: 'bold', text: text.slice(open + 2, close) });
    i = close + 2;
  }
  return out;
}
